/**
 * Web component: Product card
 * -----------------------------------------------------------------------------
 * Product card functionality.
 * - Loads additional data for Klevu product cards.
 *
 * @param {String} handle - Product handle.
 * @param {Boolean} query - Query additional product data.
 *
 */
class ProductCard extends HTMLElement {
  static observedAttributes = [
    'handle',
  ]

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super()
    this.handle = this.getAttribute('handle')
    this.query = this.getAttribute('query')

    this.nodeSelectors = {}

    this.selectors = {
      container: '[js-product-card="container"]',
      form: {
        add: '[js-product-card="add"]',
        colour: '[js-product-card="colour"]',
        container: '[js-product-card="form"]',
        error: '[js-product-card="error"]',
        option: '[js-product-card="option"]',
        select: '[js-product-card="select"]',
        midcode: '[js-product-card="mid-code"]',
      },
      hoverQuickview: '[js-product-card="hoverQuickview"]',
      imageControls: '[js-product-card="imageControls"]',
      link: '[js-product-card="link"]',
      price: '[js-product-card="price"]',
      quickviewButton: '[js-product-card="quickviewButton"]',
      wishlist: '[js-product-card="wishlist"]',
    }
  }

  /**
   * Observed attribute changed.
   * @param {String} name - Attribute name.
   * @param {String} oldValue - Old value.
   * @param {String} newValue - New value.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || oldValue === null) {
      return
    }

    switch (name) {
      case 'handle':
        this.handle = newValue
        this.queryProductCard()
        break
    }
  }

  /**
   * Query product card.
   * - Replaces whole template.
   */
  async queryProductCard() {
    try {
      if (!this.handle) {
        return
      }

      let response = await fetch(`${theme.routes.normalisedRoot}products/${this.handle}?view=product-card`)

      if (!response.ok) {
        throw response
      }

      response = await response.text()
      const parser = new DOMParser()
      const html = parser.parseFromString(response, 'text/html')
      const newContainer = html.querySelector(this.selectors.container)
      this.nodeSelectors.container.replaceWith(newContainer)

      this.setNodeSelectors()
      this.setEventListeners()
      Frame.EventBus.emit('wishlist:update')

    } catch (error) {
      window.console.error('Failed to load product', error)
    }
  }

  /**
   * Mounted.
   */
  connectedCallback() {
    this.setNodeSelectors()

    if (this.query) {
      this.queryProductData()
    } else {
      this.setEventListeners()
    }

    this.setEventBusListeners()
  }

  /**
   * Set nodeSelectors.
   */
  setNodeSelectors() {
    this.nodeSelectors = {
      container: this.querySelector(this.selectors.container),
      form: {
        add: this.querySelector(this.selectors.form.add),
        colours: [...this.querySelectorAll(this.selectors.form.colour)],
        container: this.querySelector(this.selectors.form.container),
        error: this.querySelector(this.selectors.form.error),
        options: [...this.querySelectorAll(this.selectors.form.option)],
        select: this.querySelector(this.selectors.form.select),
        midcode: this.querySelector(this.selectors.form.midcode),
      },
      hoverQuickview: this.querySelector(this.selectors.hoverQuickview),
      imageControls: this.querySelector(this.selectors.imageControls),
      link: this.querySelector(this.selectors.link),
      price: this.querySelector(this.selectors.price),
      quickviewButton: this.querySelector(this.selectors.quickviewButton),
      wishlist: this.querySelector(this.selectors.wishlist),
    }
  }

  /**
   * Query Klevu product data.
   */
  async queryProductData() {
    try {
      if (!this.handle) {
        return
      }

      const response = await fetch(`${window.theme.routes.normalisedRoot}products/${this.handle}?view=klevu`)

      if (!response.ok) {
        throw response
      }

      this.response = await response.json()
      this.renderBadges()
      this.renderPrice()
      this.renderQuickview()

      this.setNodeSelectors()
      this.setEventListeners()

    if (this.response.quickviewHidden === true && this.nodeSelectors.quickviewButton) {
      this.nodeSelectors.quickviewButton.remove()
    }

    } catch (error) {
      window.console.error('Failed to load product', error)
    }
  }

  /**
   * Render badges from query.
   * - Easier to use existing Liquid than re-build in Klevu.
   */
  renderBadges() {
    if (!this.response?.badges || !this.nodeSelectors.imageControls) {
      return
    }

    this.nodeSelectors.imageControls.insertAdjacentHTML('afterbegin', this.response.badges)
  }

  /**
   * Render price from query.
   * - Easier to use existing Liquid than re-build in Klevu.
   */
  renderPrice() {
    if (!this.response?.price || !this.nodeSelectors.price) {
      return
    }

    this.nodeSelectors.price.insertAdjacentHTML('afterbegin', this.response.price)
    this.nodeSelectors.price.classList.remove('is-loading')
  }

  /**
   * Render quickview from query.
   * - Easier to use existing Liquid than re-build in Klevu.
   */
  renderQuickview() {
    if (!this.response?.quickview || !this.nodeSelectors.hoverQuickview || this.response.quickviewHidden) {
      return
    }

    this.nodeSelectors.hoverQuickview.insertAdjacentHTML('afterbegin', this.response.quickview)
  }

  /**
   * Set event listeners.
   */
  setEventListeners() {
    this.nodeSelectors.link?.addEventListener('click', this.handleLinkClick.bind(this))
    this.nodeSelectors.quickviewButton?.addEventListener('click', this.handleQuickview.bind(this))

    this.nodeSelectors.form.colours?.forEach((element) => {
      element.addEventListener('click', this.handleColourClick.bind(this))
    })

    this.nodeSelectors.form.options?.forEach((element) => {
      element.addEventListener('change', this.handleOptionChange.bind(this))
    })

    this.nodeSelectors.form.container?.addEventListener('submit', this.handleSubmit.bind(this))
  }

  /**
   * Handle link click.
   * - Prevents linking to product when using quickview or wishlist.
   * @param {Object} event - Event.
   */
  handleLinkClick(event) {
    if (event.target.closest(this.selectors.wishlist)) {
      event.preventDefault()
    }
  }

  /**
   * Handle quickview click.
   */
  handleQuickview() {
    Frame.EventBus.emit('quickview:open', this.handle)
    this.nodeSelectors.quickviewButton.classList.add('is-loading')
  }

  /**
   * Handle colour swatch click.
   * @param {Object} event - Click event.
   */
  handleColourClick(event) {
    event.preventDefault()
    const colour = event.target.closest(this.selectors.form.colour)
    this.setAttribute('handle', colour.dataset.handle)
  }

  /**
   * Handle option change to update master select.
   * - Uses master select as source of data for variant titles and IDs.
   */
  handleOptionChange() {
    const options = this.nodeSelectors.form.options
      .map((element) => {
        if (element.type === 'radio') {
          if (element.checked === true) {
            return element.value
          }

          return false
        }

        return element.value
      })
      .filter(Boolean)

    const variantTitle = options.join(' / ')

    const variant = [...this.nodeSelectors.form.select.options]?.find((option) => {
      return option.dataset.title === variantTitle
    })

    if (!variant) {
      return
    }

    /**
     * Update form.
     */
    if (variant.dataset.available === 'true') {
      this.nodeSelectors.form.add.removeAttribute('disabled')
      this.nodeSelectors.form.add.innerText = theme.strings.collection.card.add
    } else {
      this.nodeSelectors.form.add.setAttribute('disabled', '')
      this.nodeSelectors.form.add.innerText = theme.strings.collection.card.soldOut
    }

    this.nodeSelectors.form.select.value = variant.value
    this.nodeSelectors.price.innerText = variant.dataset.price
    this.nodeSelectors.form.error.classList.add('is-hidden')

    /**
     * Update wishlist.
     */
    this.nodeSelectors.wishlist.setAttribute('data-variant', variant.value)
    this.nodeSelectors.wishlist.classList.add('iwishcheck')
    this.nodeSelectors.wishlist.classList.remove('iwishAdded')

    this.nodeSelectors.wishlist.innerHTML = `
      ${theme.icon.wishlistEmpty}

      <span class="visually-hidden">
        ${theme.strings.products.product.wishlist}
      </span>
    `

    Frame.EventBus.emit('wishlist:update')
  }

  /**
   * Handle form submit.
   * @param {Object} event - Submit event.
   */
  async handleSubmit(event) {
    try {
      event.preventDefault()
      const id = this.nodeSelectors.form.select.value

      if (!id) {
        this.nodeSelectors.form.error.classList.remove('is-hidden')
        return
      }
      const midCode = this.nodeSelectors.form.midcode?.value || '';
      this.nodeSelectors.form.error.classList.add('is-hidden')

      const items = [
        {
          id: this.nodeSelectors.form.select.value,
          quantity: 1,
          ...(midCode && {
            properties: {
              _MIDCode: midCode,
            },
          }),
        },
      ]

      const response = await fetch(theme.routes.cart.add, {
        body: JSON.stringify({ items }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      })

      if (!response.ok) {
        throw response
      }

      Frame.EventBus.emit('AjaxCart:itemUpdated')

    } catch (error) {
      window.console.error('Failed to add to cart', error)
    }
  }

  /**
   * Set EventBus listeners.
   */
  setEventBusListeners() {
    const listenInterval = setInterval(() => {
      if (!window.Frame) {
        return
      }

      clearInterval(listenInterval)

      Frame.EventBus.listen('quickview:opened', (handle) => {
        if (this.handle !== handle) {
          return
        }

        this.nodeSelectors.quickviewButton.classList.remove('is-loading')
      })

      Frame.EventBus.listen('quickview:error', (handle) => {
        if (this.handle !== handle) {
          return
        }

        this.nodeSelectors.quickviewButton.classList.add('is-error')
      })
    }, 200)
  }

  /**
   * Unmounted.
   */
  disconnectedCallback() {
    this.nodeSelectors.link?.removeEventListener('click', this.handleLinkClick.bind(this))
    this.nodeSelectors.quickviewButton?.removeEventListener('click', this.handleQuickview.bind(this))

    this.nodeSelectors.form.colours?.forEach((element) => {
      element.removeEventListener('click', this.handleColourClick.bind(this))
    })

    this.nodeSelectors.form.options?.forEach((element) => {
      element.removeEventListener('change', this.handleOptionChange.bind(this))
    })

    this.nodeSelectors.form.container?.removeEventListener('submit', this.handleSubmit.bind(this))
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
